import type { ContactFormValues, ContactProvider, ContactResult } from '@/types';

const provider = (import.meta.env.VITE_CONTACT_PROVIDER ?? 'formsubmit') as ContactProvider;
const endpoint = import.meta.env.VITE_CONTACT_ENDPOINT as string | undefined;
const formspreeId = import.meta.env.VITE_FORMSPREE_ID as string | undefined;
const contactEmail =
  (import.meta.env.VITE_CONTACT_EMAIL as string | undefined) ??
  'muhammadsufyanmalik93@gmail.com';

function assertNotSpam(values: ContactFormValues): boolean {
  return !values.website;
}

async function submitToEndpoint(values: ContactFormValues, url: string): Promise<ContactResult> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: values.name,
      email: values.email,
      subject: values.subject,
      message: values.message,
      _subject: `Portfolio contact: ${values.subject}`,
      _replyto: values.email,
      _template: 'table',
    }),
  });

  if (!response.ok) {
    return {
      ok: false,
      message: 'Unable to send your message right now. Please try again or email me directly.',
    };
  }

  try {
    const data = (await response.json()) as {
      error?: string | false;
      success?: string | boolean;
      message?: string;
    };

    if (data.error) {
      return { ok: false, message: String(data.error) };
    }

    if (data.success === false) {
      return {
        ok: false,
        message: data.message ?? 'Unable to send your message right now.',
      };
    }
  } catch {
    // Some endpoints may return an empty body on success.
  }

  return { ok: true, message: 'Message sent. I will get back to you as soon as possible.' };
}

function submitViaMailto(values: ContactFormValues): ContactResult {
  const subject = encodeURIComponent(values.subject);
  const body = encodeURIComponent(
    `Name: ${values.name}\nEmail: ${values.email}\n\n${values.message}`,
  );
  window.location.href = `mailto:${contactEmail}?subject=${subject}&body=${body}`;
  return {
    ok: true,
    message: 'Your email client should open with the message drafted. Send it when you are ready.',
  };
}

/**
 * Contact submission abstraction.
 * Default provider is FormSubmit (works from React without PHP).
 * Configure with Vite env vars — see `.env.example` and README.
 */
export async function submitContactForm(values: ContactFormValues): Promise<ContactResult> {
  if (!assertNotSpam(values)) {
    return { ok: true, message: 'Message sent. I will get back to you as soon as possible.' };
  }

  switch (provider) {
    case 'endpoint': {
      if (!endpoint) {
        return {
          ok: false,
          message: 'Contact endpoint is not configured. Set VITE_CONTACT_ENDPOINT.',
        };
      }
      return submitToEndpoint(values, endpoint);
    }
    case 'formsubmit': {
      return submitToEndpoint(
        values,
        `https://formsubmit.co/ajax/${encodeURIComponent(contactEmail)}`,
      );
    }
    case 'formspree': {
      if (!formspreeId) {
        return {
          ok: false,
          message: 'Formspree is not configured. Set VITE_FORMSPREE_ID.',
        };
      }
      return submitToEndpoint(values, `https://formspree.io/f/${formspreeId}`);
    }
    case 'emailjs': {
      return {
        ok: false,
        message:
          'EmailJS selected but not wired yet. Add the EmailJS SDK call in contactService.ts or switch provider.',
      };
    }
    case 'mailto':
      return submitViaMailto(values);
    default:
      return submitToEndpoint(
        values,
        `https://formsubmit.co/ajax/${encodeURIComponent(contactEmail)}`,
      );
  }
}

export function getContactProvider(): ContactProvider {
  return provider;
}

export function getContactEmail(): string {
  return contactEmail;
}
